import { zodResolver } from '@hookform/resolvers/zod';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { useAuth } from '@/lib/auth/useAuth';
import { useLandAfterAuth } from '@/lib/auth/useLandAfterAuth';

const signInSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Password is at least 8 characters'),
});
type SignInValues = z.infer<typeof signInSchema>;

const magicLinkSchema = z.object({ email: z.string().email('Enter a valid email') });
type MagicLinkValues = z.infer<typeof magicLinkSchema>;

interface SignInSearch {
  next?: string;
  // Legacy param still emitted by older links; `next` wins when both are present.
  redirect?: string;
}

export const Route = createFileRoute('/auth/sign-in')({
  validateSearch: (search): SignInSearch => ({
    next: typeof search.next === 'string' ? search.next : undefined,
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: SignInRoute,
});

function SignInRoute() {
  const { signInWithEmail, signInWithMagicLink, signInWithGoogle } = useAuth();
  const { next, redirect } = Route.useSearch();
  const rawNext = next ?? redirect;
  const land = useLandAfterAuth();
  const [mode, setMode] = useState<'password' | 'magic'>('password');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);

  const passwordForm = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  const magicForm = useForm<MagicLinkValues>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: { email: '' },
  });

  const onPasswordSubmit = passwordForm.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await signInWithEmail(values.email, values.password);
      await land(rawNext);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to sign in');
    }
  });

  const onMagicSubmit = magicForm.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await signInWithMagicLink(values.email, rawNext);
      setMagicSent(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to send magic link');
    }
  });

  const onGoogle = async () => {
    setSubmitError(null);
    try {
      await signInWithGoogle(rawNext);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to start Google sign-in');
    }
  };

  if (magicSent) {
    return (
      <AuthLayout
        title="Magic link sent"
        subtitle={`We sent a sign-in link to ${magicForm.getValues('email')}. Check your inbox.`}
      >
        <button
          type="button"
          onClick={() => setMagicSent(false)}
          className="mt-2 text-sm text-oxblood hover:underline"
        >
          Try a different email
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back.">
      {mode === 'password' ? (
        <form onSubmit={onPasswordSubmit} noValidate className="space-y-4">
          <Field
            id="email"
            label="Email"
            type="email"
            autoComplete="email"
            error={passwordForm.formState.errors.email?.message}
            {...passwordForm.register('email')}
          />
          <Field
            id="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            error={passwordForm.formState.errors.password?.message}
            {...passwordForm.register('password')}
          />
          {submitError && (
            <p role="alert" aria-live="polite" className="text-sm text-signal">
              {submitError}
            </p>
          )}
          <button
            type="submit"
            disabled={passwordForm.formState.isSubmitting}
            className="w-full rounded-md bg-ink px-4 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
          >
            {passwordForm.formState.isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
          <button
            type="button"
            onClick={() => setMode('magic')}
            className="block w-full text-center text-xs uppercase tracking-[0.12em] text-ink/50 hover:text-oxblood"
          >
            Send me a magic link instead
          </button>
        </form>
      ) : (
        <form onSubmit={onMagicSubmit} noValidate className="space-y-4">
          <Field
            id="magic-email"
            label="Email"
            type="email"
            autoComplete="email"
            error={magicForm.formState.errors.email?.message}
            {...magicForm.register('email')}
          />
          {submitError && (
            <p role="alert" aria-live="polite" className="text-sm text-signal">
              {submitError}
            </p>
          )}
          <button
            type="submit"
            disabled={magicForm.formState.isSubmitting}
            className="w-full rounded-md bg-ink px-4 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
          >
            {magicForm.formState.isSubmitting ? 'Sending…' : 'Send magic link'}
          </button>
          <button
            type="button"
            onClick={() => setMode('password')}
            className="block w-full text-center text-xs uppercase tracking-[0.12em] text-ink/50 hover:text-oxblood"
          >
            Sign in with password instead
          </button>
        </form>
      )}

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-ink/10" />
        <span className="font-sans text-xs uppercase tracking-[0.18em] text-ink/40">or</span>
        <span className="h-px flex-1 bg-ink/10" />
      </div>

      <button
        type="button"
        onClick={() => void onGoogle()}
        className="w-full rounded-md border border-ink/20 px-4 py-2.5 font-sans text-sm font-medium text-ink hover:border-oxblood hover:text-oxblood"
      >
        Continue with Google
      </button>

      <div className="mt-6 space-y-2 text-center text-sm">
        <p>
          <Link to="/auth/reset-password" className="text-ink/60 hover:text-oxblood">
            Forgot your password?
          </Link>
        </p>
        <p className="text-ink/70">
          New here?{' '}
          <Link to="/auth/sign-up" className="text-oxblood hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}
const Field = (() => {
  const C = ({ id, label, error, ...rest }: FieldProps) => (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
      >
        {label}
      </label>
      <input
        id={id}
        {...rest}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-signal">
          {error}
        </p>
      )}
    </div>
  );
  return C;
})();
